import React, { useCallback, useState, useEffect } from 'react';
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import Button from '@/components/CustomButtons/Button';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Typography,
  MenuItem,
  Autocomplete,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Delete } from '@mui/icons-material';
import { REFUND_TYPES } from '../../../utility';
import { InputLabel, Select } from '@mui/material';
import CopyFrom from './CopyFrom';
import AudienceConfiguration from './AudienceConfiguration';
import CustomPaymentTypeMappings from './CustomPaymentTypeMappings';
import FieldErrorText, {
  fieldErrorHelperText,
} from '@/components/FieldErrorText/FieldErrorText';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';

export default function CreateYourMapping({
  formValues,
  setFormValues,
  setUpdatingBlocks,
  updatingBlocks,
  editId,
  isUpdating,
  onDataMappingCancel,
  updateDataMapping,
  allowedRefundTypes,
  regularInputList,
  insuranceInputList,
  businessInputList,
  addSplittingValue,
  onSplittingValueChange,
  removeSplittingValue,
  copyDataMappingFrom,
  onExceptionChange,
  removeException,
  onCopyFromTo,
  availableColumns,
  customPaymentTypesForm,
  isNewCustomPaymentTypesWorkflowEnabled,
  fieldErrors = {},
  getFieldErrors = () => [],
  clearFieldError = () => {},
  generalErrors = [],
}) {
  const [consumerExpanded, setConsumerExpanded] = useState(false);
  const [businessExpanded, setBusinessExpanded] = useState(false);
  const [insuranceExpanded, setInsuranceExpanded] = useState(false);
  const [operation, setOperation] = useState('');

  useEffect(() => {
    if (formValues.insurance_data_mapping?.exceptions?.length) {
      setOperation(formValues.insurance_data_mapping.exceptions[0].operation);
    } else {
      setOperation('=');
    }
  }, [formValues.insurance_data_mapping?.exceptions]);

  const handleRefundTypeChange = useCallback(
    (e, type) => {
      const newRefundTypes = e.target.checked
        ? [...formValues.refund_types, type]
        : formValues.refund_types.filter((refundType) => refundType !== type);
      setFormValues({
        ...formValues,
        refund_types: newRefundTypes,
      });
    },
    [formValues, setFormValues],
  );

  const isAllowed = useCallback(
    (type) => {
      return allowedRefundTypes.includes(type);
    },
    [allowedRefundTypes],
  );

  const isChecked = useCallback(
    (type) => {
      return formValues.refund_types.includes(type);
    },
    [formValues, formValues.refund_types],
  );

  const atLeastPartialAllowed = allowedRefundTypes.length > 1;

  useEffect(() => {
    if (!atLeastPartialAllowed) {
      if (isAllowed(REFUND_TYPES.REGULAR)) {
        setConsumerExpanded(true);
      }
      if (isAllowed(REFUND_TYPES.INSURANCE)) {
        setInsuranceExpanded(true);
      }
      if (isAllowed(REFUND_TYPES.BUSINESS)) {
        setBusinessExpanded(true);
      }
    }
  }, [atLeastPartialAllowed, isAllowed]);

  const handleOnExpanded = useCallback(
    (type) => {
      return function () {
        if (type === REFUND_TYPES.REGULAR) {
          setConsumerExpanded((prev) => !prev);
        }
        if (type === REFUND_TYPES.INSURANCE) {
          setInsuranceExpanded((prev) => !prev);
        }
        if (type === REFUND_TYPES.BUSINESS) {
          setBusinessExpanded((prev) => !prev);
        }
      };
    },
    [atLeastPartialAllowed],
  );

  const handleOperationChange = (e) => {
    const newOperation = e.target.value;
    setOperation(newOperation);
    setFormValues((values) => {
      const updatedExceptions = values.insurance_data_mapping.exceptions?.map(
        (exception) => ({
          ...exception,
          operation: newOperation,
        }),
      );
      return {
        ...values,
        insurance_data_mapping: {
          ...values.insurance_data_mapping,
          exceptions: updatedExceptions,
        },
      };
    });
  };

  const addException = () => {
    setFormValues((values) => {
      return {
        ...values,
        insurance_data_mapping: {
          ...values.insurance_data_mapping,
          exceptions: [
            {
              subStatus: 'Payee Exception',
              columnName: 'business_payee_name',
              contains: [
                ...(values.insurance_data_mapping?.exceptions?.at(0)
                  ?.contains ?? []),
                '',
              ],
              operation: operation,
            },
          ],
        },
      };
    });
  };

  const handleCopyFromTo = (to) => {
    return (from) => {
      onCopyFromTo(from, to);
    };
  };

  const addImmediatePaymentDesignator = () => {
    clearFieldError('immediate_payment_designators');
    setFormValues((values) => ({
      ...values,
      immediate_payment_designators: [
        ...(values.immediate_payment_designators || []),
        '',
      ],
    }));
  };

  const onImmediatePaymentDesignatorChange = (e, index) => {
    clearFieldError('immediate_payment_designators');
    const updatedValues = formValues.immediate_payment_designators.map(
      (value, i) => (i === index ? e.target.value : value),
    );
    setFormValues({
      ...formValues,
      immediate_payment_designators: updatedValues,
    });
  };

  const removeImmediatePaymentDesignator = (index) => {
    clearFieldError('immediate_payment_designators');
    const updatedValues = formValues.immediate_payment_designators.filter(
      (_, i) => i !== index,
    );
    setFormValues({
      ...formValues,
      immediate_payment_designators: updatedValues,
    });
  };

  const addAudienceDesignator = () => {
    clearFieldError('audience_designators');
    setFormValues((values) => ({
      ...values,
      audience_designators: [...(values.audience_designators || []), ''],
    }));
  };

  const onAudienceDesignatorChange = (value, index) => {
    clearFieldError('audience_designators');
    const updatedValues = formValues.audience_designators.map((v, i) =>
      i === index ? value : v,
    );
    setFormValues({
      ...formValues,
      audience_designators: updatedValues,
    });
  };

  const removeAudienceDesignator = (index) => {
    clearFieldError('audience_designators');
    const updatedValues = formValues.audience_designators.filter(
      (_, i) => i !== index,
    );
    setFormValues({
      ...formValues,
      audience_designators: updatedValues,
    });
  };

  const handleIsSingleAudienceChange = (e) => {
    clearFieldError('is_single_audience');
    clearFieldError('audience_id');
    setFormValues({
      ...formValues,
      is_single_audience: e.target.checked,
      audience_id: e.target.checked ? formValues.audience_id : '',
    });
  };

  const handleSelectedAudienceChange = (e) => {
    clearFieldError('audience_id');
    setFormValues({
      ...formValues,
      audience_id: e.target.value,
    });
  };

  return (
    <Card className='dashboardCard table_card_height'>
      <CardBody
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
        }}>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <h3 className='subHeading'>Create your mapping</h3>
            <FormErrorSummary errors={generalErrors} />
            <h2 style={{ fontSize: '17px' }} className='page-heading'>
              In the email / letter templates, please use the values in &quot;
              {'{{}}'}&quot; including the double curly brackets to populate
              fields:
            </h2>
            <AudienceConfiguration
              formValues={formValues}
              isUpdating={isUpdating}
              onAudienceDesignatorChange={onAudienceDesignatorChange}
              removeAudienceDesignator={removeAudienceDesignator}
              addAudienceDesignator={addAudienceDesignator}
              isSingleAudience={formValues.is_single_audience}
              selectedAudienceId={formValues.audience_id}
              onIsSingleAudienceChange={handleIsSingleAudienceChange}
              onSelectedAudienceChange={handleSelectedAudienceChange}
              fieldErrors={fieldErrors}
              getFieldErrors={getFieldErrors}
            />
            <GridContainer>
              <GridItem xs={12} sm={12} md={6}>
                <div className='custom-select'>
                  {isNewCustomPaymentTypesWorkflowEnabled &&
                  customPaymentTypesForm ? (
                    <div style={{ marginTop: '10px', width: '100%' }}>
                      <Autocomplete
                        multiple
                        id='custom-payment-types'
                        options={customPaymentTypesForm}
                        getOptionLabel={(option) => option.name}
                        value={customPaymentTypesForm.filter((type) =>
                          formValues.custom_payment_type_ids?.includes(type.id),
                        )}
                        onChange={(event, newValue) => {
                          clearFieldError('custom_payment_type_ids');
                          setFormValues({
                            ...formValues,
                            custom_payment_type_ids: newValue.map(
                              (type) => type.id,
                            ),
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant='standard'
                            label='Custom Payment Types'
                            placeholder='Select Custom Payment Types'
                            error={!!fieldErrors.custom_payment_type_ids}
                            helperText={fieldErrorHelperText(
                              getFieldErrors('custom_payment_type_ids'),
                            )}
                          />
                        )}
                        disabled={!isUpdating('data-mapping')}
                      />
                    </div>
                  ) : (
                    atLeastPartialAllowed && (
                      <FormControl
                        component='fieldset'
                        error={!!fieldErrors.refund_types}>
                        <FormLabel component='legend'>
                          Payee category types In File
                        </FormLabel>
                        <FormGroup row>
                          {isAllowed(REFUND_TYPES.REGULAR) && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isChecked(REFUND_TYPES.REGULAR)}
                                  onChange={(e) =>
                                    handleRefundTypeChange(
                                      e,
                                      REFUND_TYPES.REGULAR,
                                    )
                                  }
                                  name={REFUND_TYPES.REGULAR}
                                  disabled={
                                    !isUpdating('data-mapping') ||
                                    !allowedRefundTypes.includes(
                                      REFUND_TYPES.REGULAR,
                                    )
                                  }
                                />
                              }
                              label='Consumer Payments'
                            />
                          )}
                          {isAllowed(REFUND_TYPES.INSURANCE) && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isChecked(REFUND_TYPES.INSURANCE)}
                                  onChange={(e) =>
                                    handleRefundTypeChange(
                                      e,
                                      REFUND_TYPES.INSURANCE,
                                    )
                                  }
                                  name={REFUND_TYPES.INSURANCE}
                                  disabled={
                                    !isUpdating('data-mapping') ||
                                    !allowedRefundTypes.includes(
                                      REFUND_TYPES.INSURANCE,
                                    )
                                  }
                                />
                              }
                              label='Insurance Payments'
                            />
                          )}
                          {isAllowed(REFUND_TYPES.BUSINESS) && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isChecked(REFUND_TYPES.BUSINESS)}
                                  onChange={(e) =>
                                    handleRefundTypeChange(
                                      e,
                                      REFUND_TYPES.BUSINESS,
                                    )
                                  }
                                  name={REFUND_TYPES.BUSINESS}
                                  disabled={
                                    !isUpdating('data-mapping') ||
                                    !allowedRefundTypes.includes(
                                      REFUND_TYPES.BUSINESS,
                                    )
                                  }
                                />
                              }
                              label='Business Payments'
                            />
                          )}
                        </FormGroup>
                        <FieldErrorText
                          messages={getFieldErrors('refund_types')}
                        />
                      </FormControl>
                    )
                  )}
                </div>
              </GridItem>
              <GridItem xs={6} sm={6} md={6}></GridItem>

              {!isNewCustomPaymentTypesWorkflowEnabled && (
                <GridContainer>
                  {isChecked(REFUND_TYPES.REGULAR) && (
                    <GridItem xs={12} sm={12} md={4}>
                      <GridContainer>
                        {formValues.regular_splitting_value.map(
                          (splittingValue, index) => (
                            <GridItem xs={12} sm={12} md={12} key={index}>
                              <CustomInput
                                type='text'
                                labelText={`Consumer Payment Filter`}
                                onChange={(e) =>
                                  onSplittingValueChange(
                                    e,
                                    index,
                                    REFUND_TYPES.REGULAR,
                                  )
                                }
                                value={splittingValue}
                                name='splittingValue'
                                formControlProps={{}}
                                disabled={!isUpdating('data-mapping')}
                              />
                              <Delete
                                onClick={() =>
                                  removeSplittingValue(
                                    index,
                                    REFUND_TYPES.REGULAR,
                                  )
                                }
                                style={{ cursor: 'pointer' }}
                              />
                            </GridItem>
                          ),
                        )}
                      </GridContainer>
                      <GridItem xs={12} sm={12} md={4} style={{ padding: 0 }}>
                        <Button
                          color='primary'
                          disabled={!isUpdating('data-mapping')}
                          onClick={() =>
                            addSplittingValue(REFUND_TYPES.REGULAR)
                          }>
                          Add Consumer Payment Filter
                        </Button>
                        <FieldErrorText
                          messages={getFieldErrors('regular_splitting_value')}
                        />
                      </GridItem>
                    </GridItem>
                  )}

                  {isChecked(REFUND_TYPES.BUSINESS) && (
                    <GridItem xs={12} sm={12} md={4}>
                      <GridContainer>
                        {formValues.business_splitting_value.map(
                          (splittingValue, index) => (
                            <GridItem xs={12} sm={12} md={12} key={index}>
                              <CustomInput
                                type='text'
                                labelText={`Business Payment Filter`}
                                onChange={(e) =>
                                  onSplittingValueChange(
                                    e,
                                    index,
                                    REFUND_TYPES.BUSINESS,
                                  )
                                }
                                value={splittingValue}
                                name='splittingValue'
                                formControlProps={{}}
                                disabled={!isUpdating('data-mapping')}
                              />
                              <Delete
                                onClick={() =>
                                  removeSplittingValue(
                                    index,
                                    REFUND_TYPES.BUSINESS,
                                  )
                                }
                                style={{ cursor: 'pointer' }}
                              />
                            </GridItem>
                          ),
                        )}
                      </GridContainer>
                      <GridItem xs={12} sm={12} md={4} style={{ padding: 0 }}>
                        <Button
                          color='primary'
                          disabled={!isUpdating('data-mapping')}
                          onClick={() =>
                            addSplittingValue(REFUND_TYPES.BUSINESS)
                          }>
                          Add Business Payment Filter
                        </Button>
                        <FieldErrorText
                          messages={getFieldErrors('business_splitting_value')}
                        />
                      </GridItem>
                    </GridItem>
                  )}

                  {isChecked(REFUND_TYPES.INSURANCE) && (
                    <GridItem xs={12} sm={12} md={4}>
                      <GridContainer>
                        {formValues.insurance_splitting_value.map(
                          (splittingValue, index) => (
                            <GridItem xs={12} sm={12} md={12} key={index}>
                              <CustomInput
                                type='text'
                                labelText={`Insurance Filter`}
                                onChange={(e) =>
                                  onSplittingValueChange(
                                    e,
                                    index,
                                    REFUND_TYPES.INSURANCE,
                                  )
                                }
                                value={splittingValue}
                                name='splittingValue'
                                formControlProps={{}}
                                disabled={!isUpdating('data-mapping')}
                              />
                              <Delete
                                onClick={() =>
                                  removeSplittingValue(
                                    index,
                                    REFUND_TYPES.INSURANCE,
                                  )
                                }
                                style={{ cursor: 'pointer' }}
                              />
                            </GridItem>
                          ),
                        )}
                      </GridContainer>
                      <GridItem xs={12} sm={12} md={4} style={{ padding: 0 }}>
                        <Button
                          color='primary'
                          disabled={!isUpdating('data-mapping')}
                          onClick={() =>
                            addSplittingValue(REFUND_TYPES.INSURANCE)
                          }>
                          Add Insurance Filter
                        </Button>
                        <FieldErrorText
                          messages={getFieldErrors('insurance_splitting_value')}
                        />
                      </GridItem>
                    </GridItem>
                  )}
                </GridContainer>
              )}
            </GridContainer>

            {isNewCustomPaymentTypesWorkflowEnabled && (
              <CustomPaymentTypeMappings
                formValues={formValues}
                setFormValues={setFormValues}
                isUpdating={isUpdating}
                customPaymentTypesForm={customPaymentTypesForm}
                dbColumns={availableColumns}
                fieldErrors={fieldErrors}
                getFieldErrors={getFieldErrors}
                clearFieldError={clearFieldError}
              />
            )}

            {!isNewCustomPaymentTypesWorkflowEnabled && (
              <>
                {isChecked(REFUND_TYPES.REGULAR) && (
                  <Accordion
                    expanded={consumerExpanded}
                    style={{ marginTop: '20px' }}
                    key={'Consumer Payments'}
                    defaultExpanded={false}
                    disabled={!isUpdating('data-mapping')}
                    onChange={handleOnExpanded(REFUND_TYPES.REGULAR)}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls='panel2-content'
                      id='panel2-header'>
                      <Typography>{'Consumer Payments'}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Card>
                        <CardBody>
                          {isChecked(REFUND_TYPES.INSURANCE) && (
                            <Button
                              color='primary'
                              disabled={!isUpdating('data-mapping')}
                              onClick={() =>
                                copyDataMappingFrom(REFUND_TYPES.INSURANCE)
                              }>
                              Copy From Below
                            </Button>
                          )}
                          <GridContainer
                            style={{ justifyContent: 'space-between' }}>
                            {regularInputList}
                          </GridContainer>
                        </CardBody>
                      </Card>
                    </AccordionDetails>
                  </Accordion>
                )}

                {isChecked(REFUND_TYPES.INSURANCE) && (
                  <Accordion
                    expanded={insuranceExpanded}
                    key={REFUND_TYPES.INSURANCE}
                    defaultExpanded={false}
                    disabled={!isUpdating('data-mapping')}
                    onChange={handleOnExpanded(REFUND_TYPES.INSURANCE)}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls='panel2-content'
                      id='panel2-header'>
                      <Typography>{'Insurance'}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Card>
                        <CardBody>
                          {isChecked(REFUND_TYPES.REGULAR) && (
                            <Button
                              color='primary'
                              disabled={!isUpdating('data-mapping')}
                              onClick={() =>
                                copyDataMappingFrom(REFUND_TYPES.REGULAR)
                              }>
                              Copy From Above
                            </Button>
                          )}
                          <GridContainer
                            style={{ justifyContent: 'space-between' }}>
                            {insuranceInputList}
                          </GridContainer>
                          <GridItem xs={6} sm={6} md={6}>
                            <div className='custom-select'>
                              <FormControl variant='standard'>
                                <InputLabel id='operation'>
                                  Business Payee Name Matching
                                </InputLabel>
                                <Select
                                  labelId='operation'
                                  value={operation}
                                  onChange={handleOperationChange}
                                  disabled={!isUpdating('data-mapping')}
                                  name='operation'
                                  className='full-width-elem'>
                                  <MenuItem value='='>
                                    Business Payee Name Below Are Exceptions
                                  </MenuItem>
                                  <MenuItem value='!='>
                                    Business Payee Name Below Are Not Exceptions
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </div>
                          </GridItem>
                          <GridItem xs={12} sm={12} md={12}>
                            <GridContainer>
                              {formValues.insurance_data_mapping?.exceptions
                                ?.at(0)
                                ?.contains?.map((exceptionValue, index) => (
                                  <GridItem xs={6} sm={6} md={6} key={index}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}>
                                      <div
                                        style={{
                                          width: '80%',
                                        }}>
                                        <CustomInput
                                          type='text'
                                          labelText={`Business Payee Name`}
                                          onChange={(e) =>
                                            onExceptionChange(e, index)
                                          }
                                          value={exceptionValue}
                                          name='exceptionValue'
                                          formControlProps={{
                                            fullWidth: true,
                                          }}
                                          disabled={!isUpdating('data-mapping')}
                                        />
                                      </div>
                                      <Delete
                                        onClick={() => removeException(index)}
                                        style={{
                                          cursor: 'pointer',
                                          marginTop: '30px',
                                        }}
                                      />
                                    </div>
                                  </GridItem>
                                ))}
                            </GridContainer>
                            <GridItem
                              xs={12}
                              sm={12}
                              md={6}
                              style={{ padding: 0 }}>
                              <Button
                                color='primary'
                                disabled={!isUpdating('data-mapping')}
                                onClick={addException}>
                                Add Business Payee Name
                              </Button>
                            </GridItem>
                          </GridItem>
                        </CardBody>
                      </Card>
                    </AccordionDetails>
                  </Accordion>
                )}

                {isChecked(REFUND_TYPES.BUSINESS) && (
                  <Accordion
                    expanded={businessExpanded}
                    key={'Business Payments'}
                    defaultExpanded={false}
                    disabled={!isUpdating('data-mapping')}
                    onChange={handleOnExpanded(REFUND_TYPES.BUSINESS)}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls='panel2-content'
                      id='panel2-header'>
                      <Typography>{'Business Payments'}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Card>
                        <CardBody>
                          <CopyFrom
                            current={REFUND_TYPES.BUSINESS}
                            available={formValues.refund_types}
                            onSelected={handleCopyFromTo(REFUND_TYPES.BUSINESS)}
                          />
                          <GridContainer
                            style={{ justifyContent: 'space-between' }}>
                            {businessInputList}
                          </GridContainer>
                        </CardBody>
                      </Card>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}
          </GridItem>
        </GridContainer>
        {!isNewCustomPaymentTypesWorkflowEnabled && (
          <GridContainer>
            <GridItem xs={12} sm={12} md={4} style={{ marginTop: '20px' }}>
              {formValues.immediate_payment_designators?.map((value, index) => (
                <GridItem xs={12} sm={12} md={12} key={index}>
                  <CustomInput
                    type='text'
                    labelText={`Immediate Payment Designator`}
                    onChange={(e) =>
                      onImmediatePaymentDesignatorChange(e, index)
                    }
                    value={value}
                    name='immediatePaymentDesignator'
                    formControlProps={{}}
                    disabled={!isUpdating('data-mapping')}
                  />
                  <Delete
                    onClick={() => removeImmediatePaymentDesignator(index)}
                    style={{ cursor: 'pointer' }}
                  />
                </GridItem>
              ))}
              <FieldErrorText
                messages={getFieldErrors('immediate_payment_designators')}
              />
              <GridItem xs={12} sm={12} md={4} style={{ padding: 0 }}>
                <Button
                  color='primary'
                  disabled={!isUpdating('data-mapping')}
                  onClick={addImmediatePaymentDesignator}>
                  Add Immediate Payment Designator
                </Button>
              </GridItem>
            </GridItem>
          </GridContainer>
        )}
        {editId && !updatingBlocks.includes('data-mapping') && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px',
            }}>
            <Button
              style={{ marginTop: '10px', marginBottom: '-10px' }}
              onClick={() =>
                setUpdatingBlocks([...updatingBlocks, 'data-mapping'])
              }
              color='primary'>
              Edit
            </Button>
          </div>
        )}

        {updatingBlocks.includes('data-mapping') && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px',
            }}>
            <Button
              style={{ marginTop: '10px', marginBottom: '-10px' }}
              onClick={onDataMappingCancel}
              color='danger'>
              Cancel
            </Button>
            <Button
              style={{ marginTop: '10px', marginBottom: '-10px' }}
              onClick={updateDataMapping}
              color='primary'>
              Save Data Mapping
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
